import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { InboxIcon } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../ui/PageHeader';
import { TabPanel, Tabs } from '../ui/Tabs';
import { EmptyState } from '../ui/EmptyState';
import { Spinner } from '../../ui/Spinner';
import { InboxOrderRow } from './InboxOrderRow';
import { InboxBookingRow } from './InboxBookingRow';
import { InboxLeads } from './InboxLeads';
import { useInbox } from './useInbox';
import { isPaid, isToPack, nextStatus } from './inboxRules';
import { listBox } from '../ui/styles';
import { LoadFailed } from '../ui/LoadState';
import type { InboxBooking, InboxOrder } from '../../core/types/adminRequests';
import type { Tracking } from '../../core/types/account';
const tabs = [{
  key: 'bestellingen',
  label: 'Bestellingen'
}, {
  key: 'afspraken',
  label: 'Afspraakaanvragen'
}, {
  key: 'advies',
  label: 'Huidadvies & nieuwsbrief'
}] as const;

/** Alles wat via de site en Mijn omgeving binnenkomt. Elke actie bewaart direct. */
export function Inbox() {
  const {
    inbox,
    status,
    reload,
    advanceOrder,
    confirm
  } = useInbox();
  const [params, setParams] = useSearchParams();
  const tab = tabs.find(t => t.key === params.get('tab'))?.key ?? 'bestellingen';
  const [openId, setOpenId] = useState<string | null>(null);
  const advance = async (o: InboxOrder, tracking?: Tracking) => {
    const next = nextStatus[o.status];
    if (!next) return;
    if (!(await advanceOrder(o, next, tracking))) return;
    if (next !== 'verzonden') toast.success('Gemarkeerd als ingepakt');else if (tracking) toast.success(`${o.name} krijgt een mail met de ${tracking.carrier}-volglink`, {
      description: 'Hij staat ook in Mijn omgeving.'
    });else toast.success(`${o.name} krijgt een mail dat het pakket onderweg is`, {
      description: 'Zonder track & trace. Een code kun je later niet meer toevoegen.'
    });
  };
  const confirmBooking = async (b: InboxBooking) => {
    if (await confirm(b)) toast.success(`${b.booking.name} krijgt een bevestiging per mail`);
  };
  return <div data-id="element-1690" data-mp="7ny3gcjaun2q">
      <PageHeader title="Bestellingen & afspraken" description="Alles wat via de site en Mijn omgeving binnenkomt." data-id="element-1691" data-mp="2na515sqr3yh" />

      <Tabs id="inbox" label="Soort bericht" className="mb-6" value={tab} onChange={key => setParams({
      tab: key
    }, {
      replace: true
    })} tabs={tabs.map(t => ({
      ...t,
      count: t.key === 'bestellingen' ? inbox?.orders.filter(isToPack).length || undefined : t.key === 'afspraken' ? inbox?.bookings.filter(b => b.status === 'aanvraag').length || undefined : undefined
    }))} data-id="element-1692" data-mp="gn1qfrll7mmi" />

      <TabPanel id="inbox" value={tab} data-id="element-1693" data-mp="vc7nb8wyd0r8">
      {status === 'loading' && !inbox ? <div className="flex items-center gap-2 py-12 text-[14px] text-muted" role="status" data-id="element-1694" data-mp="19cidsaucodbv">
          <Spinner data-id="element-1695" data-mp="14d5jx652a5zs" /> Inbox laden…
        </div> : status === 'error' || !inbox ? <LoadFailed icon={InboxIcon} title="De inbox kon niet laden" onRetry={reload} data-id="element-1696" data-mp="1nxw9icv5pir5" /> : <>
          {tab === 'bestellingen' && (inbox.orders.length === 0 ? <EmptyState icon={InboxIcon} title="Nog geen bestellingen" text="Nieuwe bestellingen verschijnen hier vanzelf." data-id="element-1697" data-mp="1ey0w01e4pbny" /> : <ul className={listBox} data-id="element-1698" data-mp="1ix9nt0plyzgv">
                {/* Onbetaalde bestellingen onderaan: ze bestaan al (Mollie), maar gaan nog niet de doos in. */}
                {[...inbox.orders.filter(isPaid), ...inbox.orders.filter(o => !isPaid(o))].map(o => <InboxOrderRow key={o.id} order={o} open={openId === o.id} onToggle={() => setOpenId(openId === o.id ? null : o.id)} onAdvance={tracking => advance(o, tracking)} data-id="element-1699" data-mp="va9wke01h1rm" />)}
              </ul>)}

          {tab === 'afspraken' && (inbox.bookings.length === 0 ? <EmptyState icon={InboxIcon} title="Geen openstaande aanvragen" text="Nieuwe aanvragen en verzetverzoeken uit Mijn omgeving komen hier binnen." data-id="element-1700" data-mp="j6flz08wzyng" /> : <ul className={listBox} data-id="element-1701" data-mp="5g2e0xw0wjy4">
                {inbox.bookings.map(b => <InboxBookingRow key={b.id} booking={b} onConfirm={() => confirmBooking(b)} data-id="element-1702" data-mp="gpt4ioppmy6" />)}
              </ul>)}

          {tab === 'advies' && <InboxLeads leads={inbox.leads} newsletter={inbox.newsletter} data-id="element-1703" data-mp="3rlfw4f878mmw" />}
        </>}
      </TabPanel>
    </div>;
}